<template>
<div class="flex min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors duration-300">

  <!-- Sidebar -->
  <HrSidebar :isOpen="isSidebarOpen" />

  <!-- Main -->
  <main 
    class="flex-1 overflow-x-hidden transition-all duration-300"
    :class="{'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen}"
  >

    <!-- Navbar -->
    <header class="fixed top-0 z-50 w-full bg-white dark:bg-gray-800 shadow-sm">
      <HrTopbar
        :page-title="currentPageTitle"
        :is-sidebar-open="isSidebarOpen"
        :is-dark-mode="isDarkMode"
        @toggle-sidebar="toggleSidebar"
        @toggle-dark-mode="toggleDarkMode"
      />
    </header>

    <!-- Content -->
    <div class="p-6 mt-16">

      <!-- Top Bar -->
      <div class="flex justify-between items-center mb-8">
        <input
          v-model="search"
          type="text"
          placeholder="Search main questions..."
          class="border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 w-72 focus:ring focus:ring-blue-300 dark:bg-gray-700 dark:text-white"
        />

        <button
          @click="openCreateMain"
          class="bg-[#74bd54] text-white px-5 py-2 rounded-lg hover:bg-[#63a43f] shadow flex items-center gap-1"
        >
          + Add Main Question
        </button>
      </div>

      <!-- Main Questions Grid -->
      <div v-if="filteredMainQuestions.length" class="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
        <div
          v-for="main in filteredMainQuestions"
          :key="main.id"
          class="bg-white dark:bg-gray-800 p-6 rounded-xl shadow hover:shadow-xl transition"
        >
          <h3 class="font-semibold text-gray-800 dark:text-white mb-3">
            {{ main.question_text }}
          </h3>

          <div class="flex gap-2 mb-4">
            <span class="bg-blue-100 text-blue-700 text-xs px-3 py-1 rounded">
              {{ main.type }}
            </span>
            <span class="bg-green-100 text-green-700 text-xs px-3 py-1 rounded">
              Main
            </span>
          </div>

          <p class="text-xs text-gray-500 mb-4">{{ main.category }}</p>

          <div class="text-sm text-gray-600 dark:text-gray-400 mb-5">
            Contains <span class="font-semibold">{{ getSubCount(main.id) }}</span> subquestion{{ getSubCount(main.id) !== 1 ? 's' : '' }}
          </div>

          <div class="flex gap-2">
            <button
              @click="openAddSubQuestion(main)"
              class="flex-1 bg-[#035a76] text-white py-2.5 rounded-lg text-sm hover:bg-[#024a5f] transition"
            >
              + Add Subquestion
            </button>
            <button
              @click="viewSubQuestions(main)"
              class="flex-1 border border-gray-300 dark:border-gray-600 py-2.5 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition"
            >
              View Subquestions
            </button>
          </div>

          <div class="flex justify-between text-xs mt-6 pt-4 border-t dark:border-gray-700">
            <button @click="editMainQuestion(main)" class="text-blue-600 hover:underline">
              Edit Main
            </button>
            <button @click="deleteMainQuestion(main.id)" class="text-red-500 hover:underline">
              Delete
            </button>
          </div>
        </div>
      </div>

      <div v-else class="text-center text-gray-400 mt-20">
        No main questions created yet. Click "Add Main Question" to start.
      </div>

      <!-- ====================== MODALS ====================== -->

      <!-- 1. Original Main Question Modal (Kept as you requested) -->
      <Transition name="modal">
        <div v-if="showMainModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">
            <h3 class="text-lg font-semibold text-[#035a76] mb-6">
              {{ editing ? 'Edit Main Question' : 'Create Main Question' }}
            </h3>

            <form @submit.prevent="saveMainQuestion" class="space-y-4">
              <input
                v-model="form.question_text"
                type="text"
                placeholder="Enter Question"
                required
                class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
              />

              <select v-model="form.type" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white">
                <option value="text">Short Answer</option>
                <option value="textarea">Long Answer</option>
                <option value="rating">Rating (1–5)</option>
              </select>

              <input
                v-model="form.category"
                type="text"
                placeholder="Category (e.g. HR, Technical)"
                class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
              />

              <div class="flex justify-end space-x-3 pt-4">
                <button type="button" @click="closeMainModal" class="px-4 py-2 bg-gray-300 rounded">
                  Cancel
                </button>
                <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>

      <!-- 2. Add Subquestion Modal -->
      <Transition name="modal">
        <div v-if="showSubModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[420px] shadow-lg">
            <h3 class="text-lg font-semibold text-[#035a76] mb-4">Add Subquestion</h3>
            <p class="text-sm text-gray-500 mb-6">
              For: <span class="font-medium">{{ selectedMain?.question_text }}</span>
            </p>

            <form @submit.prevent="saveSubQuestion" class="space-y-4">
              <input
                v-model="subForm.question_text"
                type="text"
                placeholder="Enter Sub Question"
                required
                class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white"
              />

              <select v-model="subForm.type" class="w-full border rounded p-2 dark:bg-gray-700 dark:text-white">
                <option value="text">Short Answer</option>
                <option value="textarea">Long Answer</option>
                <option value="rating">Rating (1–5)</option>
              </select>

              <div class="flex justify-end space-x-3 pt-4">
                <button type="button" @click="closeSubModal" class="px-4 py-2 bg-gray-300 rounded">
                  Cancel
                </button>
                <button type="submit" class="px-4 py-2 bg-[#74bd54] text-white rounded hover:bg-[#63a43f]">
                  Save Subquestion
                </button>
              </div>
            </form>
          </div>
        </div>
      </Transition>

      <!-- 3. View Subquestions Modal -->
      <Transition name="modal">
        <div v-if="showViewModal" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div class="bg-white dark:bg-gray-800 rounded-xl p-6 w-[520px] shadow-lg max-h-[85vh] overflow-y-auto">
            <h3 class="text-lg font-semibold text-[#035a76] mb-2">
              Subquestions for
            </h3>
            <p class="text-gray-600 dark:text-gray-400 mb-6">{{ selectedMain?.question_text }}</p>

            <div v-if="currentSubQuestions.length" class="space-y-4 mb-6">
              <div
                v-for="(sub, index) in currentSubQuestions"
                :key="sub.id"
                class="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg"
              >
                <div class="flex justify-between items-start">
                  <div>
                    <span class="text-xs text-gray-500">{{ String.fromCharCode(97 + index) }}.</span>
                    <p class="font-medium">{{ sub.question_text }}</p>
                    <span class="text-xs text-blue-600">{{ sub.type }}</span>
                  </div>
                  <div class="flex gap-3 text-sm">
                    <button @click="editSub(sub)" class="text-blue-600 hover:underline">Edit</button>
                    <button @click="deleteSub(sub.id)" class="text-red-500 hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8 text-gray-400">
              No subquestions yet
            </div>

            <div class="flex justify-end">
              <button @click="closeViewModal" class="px-5 py-2 bg-gray-300 rounded">
                Close
              </button>
            </div>
          </div>
        </div>
      </Transition>

    </div>
  </main>
</div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue"
import api from "@/services/api"
import HrSidebar from "../../components/hr/HrSidebar.vue"
import HrTopbar from "../../components/hr/HrTopbar.vue"

const currentPageTitle = ref("Interview Questions")
const isSidebarOpen = ref(true)
const isDarkMode = ref(false)

const search = ref("")

// Mock Data
const questions = ref([
  { id: 1, question_text: "Tell me about yourself", type: "textarea", parent_id: null, category: "General" },
  { id: 2, question_text: "What are your greatest strengths?", type: "text", parent_id: null, category: "Behavioral" },
  { id: 3, question_text: "Why do you want to work with us?", type: "textarea", parent_id: null, category: "Motivation" },
  { id: 4, question_text: "Give an example of a leadership experience", type: "textarea", parent_id: 2, category: "Behavioral" },
  { id: 5, question_text: "How did you overcome that challenge?", type: "text", parent_id: 2, category: "Behavioral" },
  { id: 6, question_text: "What excites you about our mission?", type: "textarea", parent_id: 3, category: "Motivation" }
])

const showMainModal = ref(false)
const showSubModal = ref(false)
const showViewModal = ref(false)
const editing = ref(false)

const form = ref({ id: null, question_text: "", type: "text", category: "" })
const subForm = ref({ id: null, question_text: "", type: "text", category: "" })

const selectedMain = ref(null)
const currentSubQuestions = ref([])

// Computed
const filteredMainQuestions = computed(() => {
  return questions.value.filter(q => 
    !q.parent_id && 
    q.question_text.toLowerCase().includes(search.value.toLowerCase())
  )
})

const fetchQuestions = async () => {
  try {
    const response = await api.get("InterviewQuestions.php");

    questions.value = response.data;

  } catch (error) {
    console.error("Error fetching questions:", error);
  }
};

const getSubCount = (parentId) => {
  return questions.value.filter(q => q.parent_id === parentId).length
}

// Main Question Functions
const openCreateMain = () => {
  editing.value = false
  form.value = { id: null, question_text: "", type: "text", category: "" }
  showMainModal.value = true
}

const editMainQuestion = (q) => {
  editing.value = true
  form.value = { ...q }
  showMainModal.value = true
}

// const saveMainQuestion = () => {
//   if (editing.value) {
//     const index = questions.value.findIndex(q => q.id === form.value.id)
//     if (index !== -1) questions.value[index] = { ...form.value, parent_id: null }
//   } else {
//     const newId = Math.max(0, ...questions.value.map(q => q.id)) + 1
//     questions.value.push({ ...form.value, id: newId, parent_id: null })
//   }
//   closeMainModal()
// }


const saveMainQuestion = async () => {
  try {
    let response;

    if (editing.value) {
      response = await api.put("InterviewQuestions.php", form.value);

      const index = questions.value.findIndex(q => q.id === form.value.id);
      if (index !== -1) {
        questions.value[index] = {
          ...questions.value[index],
          ...form.value,
          parent_id: null
        };
      }

    } else {
      response = await api.post("InterviewQuestions.php", {
        ...form.value,
        parent_id: null
      });

      questions.value.push({
        id: response.data.main_id,
        question_text: form.value.question_text,
        type: form.value.type,
        category: form.value.category,
        parent_id: null
      });
    }
    await fetchQuestions();


    closeMainModal();

  } catch (error) {
    console.error("Error saving main question:", error);
  }
};

const deleteMainQuestion = (id) => {
  if (confirm("Delete this main question and all its subquestions?")) {
    questions.value = questions.value.filter(q => q.id !== id && q.parent_id !== id)
  }
}

const closeMainModal = () => {
  showMainModal.value = false
}

// Subquestion Functions
const openAddSubQuestion = (main) => {
  selectedMain.value = main
  subForm.value = { id: null, question_text: "", type: "text", category: main.category }
  showSubModal.value = true
}

// const saveSubQuestion = () => {
//   const newId = Math.max(0, ...questions.value.map(q => q.id)) + 1
//   questions.value.push({
//     ...subForm.value,
//     id: newId,
//     parent_id: selectedMain.value.id
//   })
//   closeSubModal()
// }
const saveSubQuestion = async () => {
  try {
    const payload = {
      question_text: subForm.value.question_text,
      type: subForm.value.type,
      category: subForm.value.category,
      parent_id: selectedMain.value.id
    };

    const response = await api.post("InterviewQuestions.php", payload);

    questions.value.push({
      id: response.data.main_id, // backend returns inserted id
      ...payload
    });

    await fetchQuestions();


    closeSubModal();

  } catch (error) {
    console.error("Error saving subquestion:", error);
  }
};
const closeSubModal = () => {
  showSubModal.value = false
  selectedMain.value = null
}

const viewSubQuestions = (main) => {
  selectedMain.value = main
  currentSubQuestions.value = questions.value.filter(q => q.parent_id === main.id)
  showViewModal.value = true
}

const closeViewModal = () => {
  showViewModal.value = false
  selectedMain.value = null
}

const deleteSub = (id) => {
  if (confirm("Delete this subquestion?")) {
    questions.value = questions.value.filter(q => q.id !== id)
    // Refresh current view
    currentSubQuestions.value = currentSubQuestions.value.filter(q => q.id !== id)
  }
}

const editSub = (sub) => {
  alert(`Edit functionality for subquestion: "${sub.question_text}"\n\nYou can expand this later.`)
}

// Dark mode & Sidebar
const toggleSidebar = () => isSidebarOpen.value = !isSidebarOpen.value

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark")
  } else {
    document.documentElement.classList.remove("dark")
  }
}

onMounted(() => {
fetchQuestions();})
</script>

<style>
.modal-enter-active, .modal-leave-active { transition: all 0.25s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; transform: scale(0.9); }
</style>